import { createSlice, createSelector, PayloadAction, createAction } from "@reduxjs/toolkit";
import { MemberDetails } from "Pages/NewPayments/MemberDetailsFormModal";
import { RootState } from "store";
import { InitiateCartPaymentResponse, InitiatePaymentResponse } from "utils/paymentSummaryTypes";
import { ConfigResponse, PurchasedPlan } from "./types";

export enum PaymentsPrerequisites {
  LOADING,
  NOT_LOGGED_IN,
  LOGGED_IN,
  NOT_ONBOARDED,
  ONBOARDED,
  DOB_ISNRI_MISMATCH,
  NO_DOB_ISNRI_MISMATCH,
  NO_PLANS_SELECTED,
  PLANS_SELECTED,
  SUCCESS
}

interface PaymentsState {
  preq: PaymentsPrerequisites;
  selectedPlans: string[];
  dob?: string;
  isNri?: boolean;
  pricingResponse?: Partial<InitiateCartPaymentResponse> | Partial<InitiatePaymentResponse>;
  configResponse?: ConfigResponse;
  promoCode?: string;
  purchasedPlans?: PurchasedPlan[];
  preqObj: {
    loggedIn?: boolean;
    onboarded?: boolean;
    noMismatch?: boolean;
    plansSelected?: boolean;
  }
}

const initialState: PaymentsState = {
  preq: PaymentsPrerequisites.LOADING,
  preqObj: {
    loggedIn: undefined,
    onboarded: undefined,
    noMismatch: undefined,
    plansSelected: undefined,
  },
  selectedPlans: [],
}

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    resetState: () => ({ ...initialState }),
    setPurchasedPlans: (state: PaymentsState, action: PayloadAction<PurchasedPlan[]>) => {
      state.purchasedPlans = action.payload;
    },
    setPreRequisites: (state: PaymentsState, action: PayloadAction<PaymentsPrerequisites>) => {
      state.preq = action.payload;

      if (action.payload === PaymentsPrerequisites.NOT_LOGGED_IN) {
        state.preqObj.loggedIn = false;
        return;
      }

      if (action.payload === PaymentsPrerequisites.LOGGED_IN) {
        state.preqObj.loggedIn = true;
        return;
      }

      if (action.payload === PaymentsPrerequisites.NOT_ONBOARDED) {
        state.preqObj.onboarded = false;
        return;
      }

      if (action.payload === PaymentsPrerequisites.ONBOARDED) {
        state.preqObj.onboarded = true;
        return;
      }

      if (action.payload === PaymentsPrerequisites.NO_PLANS_SELECTED) {
        state.preqObj.plansSelected = false;
        return;
      }

      if (action.payload === PaymentsPrerequisites.PLANS_SELECTED) {
        state.preqObj.plansSelected = true;
        return;
      }

      if (action.payload === PaymentsPrerequisites.DOB_ISNRI_MISMATCH) {
        state.preqObj.noMismatch = false;
        return;
      }

      if (action.payload === PaymentsPrerequisites.NO_DOB_ISNRI_MISMATCH) {
        state.preqObj.noMismatch = true;
        return;
      }

      if (action.payload === PaymentsPrerequisites.SUCCESS) {
        state.preqObj.plansSelected = true;
        state.preqObj.loggedIn = true;
        state.preqObj.onboarded = true;
        state.preqObj.noMismatch = true;
        return;
      }

    },
    setDobNri: (state: PaymentsState, action: PayloadAction<{ dob: string, isNri: boolean }>) => {
      const { dob, isNri } = action.payload;
      state.dob = dob;
      state.isNri = isNri;
    },
    setSelectedPlans: (state: PaymentsState, action: PayloadAction<string[]>) => {
      state.selectedPlans = action.payload;
    },
    setPricingResponse: (state: PaymentsState, action: PayloadAction<Partial<InitiateCartPaymentResponse> | Partial<InitiatePaymentResponse>>) => {
      state.pricingResponse = action.payload;
    },
    clearPricingResponse: (state: PaymentsState) => {
      state.pricingResponse = undefined;
    },
    setConfigResponse: (state: PaymentsState, action: PayloadAction<ConfigResponse>) => {
      state.configResponse = action.payload;
    },
    setPromoCode: (state: PaymentsState, action: PayloadAction<string>) => {
      state.promoCode = action.payload;
    },
    addYessToCart: (state: PaymentsState) => {
      if (state.selectedPlans.length > 0) {
        state.selectedPlans = [...state.selectedPlans, "yess"];
      } else {
        state.selectedPlans = ["yess"];
      }
    }
  }
})

// selectors
const paymentsSliceSelector = (state: RootState) => state.payments || initialState;
export const selectPreRequisites = createSelector(
  [paymentsSliceSelector, (state: RootState) => state.user.userStatus],
  (paymentsState, userStatus) => {
    // Check if we're still loading initial state
    if (
      paymentsState.preqObj.loggedIn === undefined &&
      paymentsState.preqObj.plansSelected === undefined &&
      paymentsState.preqObj.noMismatch === undefined &&
      paymentsState.preqObj.onboarded === undefined
    ) return PaymentsPrerequisites.LOADING;

    // CRITICAL: Check actual user authentication status first
    // UserStatus.LOGGED_OUT = 2, UserStatus.LOGGED_IN = 1
    const isUserActuallyLoggedIn = userStatus.includes(1); // LOGGED_IN = 1
    
    // If user is not actually logged in, always return NOT_LOGGED_IN
    if (!isUserActuallyLoggedIn) {
      return PaymentsPrerequisites.NOT_LOGGED_IN;
    }

    // If user is logged in but not onboarded
    if (!paymentsState.preqObj.onboarded)
      return PaymentsPrerequisites.NOT_ONBOARDED;

    // If user is logged in and onboarded but no plans selected
    if (!paymentsState.preqObj.plansSelected)
      return PaymentsPrerequisites.NO_PLANS_SELECTED;

    // If user is logged in, onboarded, plans selected but DOB/NRI mismatch
    // Only block if the check explicitly failed (false). Undefined means the check
    // never ran (no dob/isNRI in URL), which is fine — let it pass through.
    if (paymentsState.preqObj.noMismatch === false)
      return PaymentsPrerequisites.DOB_ISNRI_MISMATCH;

    // All conditions met
    return PaymentsPrerequisites.SUCCESS;
  }
);
export const selectDOBNRI = createSelector([paymentsSliceSelector], (state) => ({ dob: state.dob, isNRI: state.isNri }));
export const selectConfig = createSelector([paymentsSliceSelector], (state) => state.configResponse);
export const selectPromoCode = createSelector([paymentsSliceSelector], (state) => state.promoCode);
export const selectCartResponse = createSelector([paymentsSliceSelector], (state) => state.pricingResponse);
export const selectAllPurchasedPlans = createSelector([paymentsSliceSelector], (state) => state.purchasedPlans);
export const selectedPlans = createSelector([paymentsSliceSelector], (state) => {
  if (state.configResponse === undefined || state.selectedPlans.length === 0) return [];
  return state.selectedPlans.map((plan: string) => state.configResponse?.data.fetchPlanByUserInfo.plans.find((pl) => pl.id === plan));
})
export const selectOnboardingFields = createSelector((state: RootState) => state, (state: RootState) => {
  const response: Partial<MemberDetails> = {};
  if (!!state.user.userProfile?.isNRI) {
    response["isNRI"] = state.user.userProfile.isNRI;
  }

  if (state.user.will?.testatorDetails) {
    const { testatorDetails } = state.user.will!;
    response["email"] = testatorDetails!.email || undefined;
    response["fullName"] = testatorDetails!.fullName || undefined;
    response["dateOfBirth"] = testatorDetails!.dateOfBirth || undefined;
  } else {
    response["email"] = state.user.userProfile?.email || undefined;
    response["fullName"] = state.user.userProfile?.fullName || undefined;
    response["dateOfBirth"] = state.user.userProfile?.dateOfBirth || undefined;
  }

  return { ...response, dateOfBirth: response.dateOfBirth?.substring(0, 10) || undefined };
})
export const selectUserHasPlan = createSelector((state: RootState) => state, (state: RootState) => {
  const mainPlans = ['starter', 'pro', 'SMART', 'smart', 'elite', 'select'];

  const userPlans = state.user.currentUserPlansResponse?.data?.fetchPlans;
  const purchasedPlans = state.payments.purchasedPlans;

  // Prefer purchasedPlans if available; else fall back to userPlans
  const plansToCheck = (purchasedPlans && purchasedPlans.length > 0) ? purchasedPlans : userPlans;

  // Debug visibility
  console.log('ns - selectUserHasPlan -> userPlans:', userPlans);
  console.log('ns - selectUserHasPlan -> purchasedPlans:', purchasedPlans);

  if (!plansToCheck || !Array.isArray(plansToCheck)) {
    return false;
  }

  const now = new Date();

  const hasActiveRecognizedPlan = plansToCheck.some((plan: any) => {
    // Plan id can be planId (from fetchPlans) or itemId (from purchasedPlans)
    const rawId: string | undefined = plan.planId || plan.itemId;
    if (!rawId) return false;

    const planId = String(rawId).toLowerCase();
    const isRecognized = mainPlans.includes(planId);
    if (!isRecognized) return false;

    // Active check: prefer explicit expireAt if present; otherwise derive 1 year - 1 day from paymentInitiatedDate
    if (plan.expireAt) {
      const expiry = new Date(plan.expireAt);
      return expiry > now;
    }

    if (plan.paymentInitiatedDate) {
      const initiated = new Date(plan.paymentInitiatedDate);
      const expiryFallback = new Date(initiated);
      expiryFallback.setFullYear(initiated.getFullYear() + 1);
      expiryFallback.setDate(expiryFallback.getDate() - 1);
      return expiryFallback > now;
    }

    return false;
  });

  return hasActiveRecognizedPlan;
});

export const selectPurchasedPlans = createSelector([paymentsSliceSelector], (state: PaymentsState) => {
  const planDetails = [
    {
      id: "starter",
      firstLine: "Starter Will",
      secondLine: "Yellow Will",
      expiryLinePrefix: "Create by"
    },
    {
      id: "pro",
      firstLine: "Pro Will",
      secondLine: "Yellow Will + Two 30 min Legal Expert Calls",
      expiryLinePrefix: "Create by"
    },
    {
      id: "SMART",
      firstLine: "Yellow SMART On-Ground Support",
      secondLine: "Annual Subscription : Post Demise Asset Transfer Cover",
      expiryLinePrefix: "Renew it by"
    },
    {
      id: "YESS",
      firstLine: "Yellow SMART Remote Support",
      secondLine: "Annual Subscription : Post Demise Online Support",
      expiryLinePrefix: "Renew it by"
    },
    {
      id: "smart",
      firstLine: "Yellow SMART On-Ground Support",
      secondLine: "Annual Subscription : Post Demise Asset Transfer Cover",
      expiryLinePrefix: "Renew it by"
    },
    {
      id: "yess",
      firstLine: "Yellow SMART Remote Support",
      secondLine: "Annual Subscription : Post Demise Online Support",
      expiryLinePrefix: "Renew it by"
    },
  ];

  return (state.purchasedPlans || [])
    .map((plan: PurchasedPlan) => {
      const details = planDetails.find(pl => pl.id === plan.itemId);
      const initiatedDate = new Date(plan.paymentInitiatedDate);
      const expiryDate = new Date(initiatedDate);
      expiryDate.setFullYear(initiatedDate.getFullYear() + 1);
      expiryDate.setDate(expiryDate.getDate() - 1);

      return {
        ...details,
        startDate: initiatedDate,
        expiry: expiryDate
      };
    });
})

// actions
export const {
  setPricingResponse,
  clearPricingResponse,
  setSelectedPlans,
  setDobNri,
  setPreRequisites,
  setPurchasedPlans,
  resetState,
  setConfigResponse,
  setPromoCode,
  addYessToCart
} = paymentsSlice.actions;

export const selectPlans = createAction<PayloadAction<string>>('payments/setSelectedPlans');
export const paymentPageLoaded = createAction('payments/pageLoaded');
export const loadConfig = createAction('payments/loadConfig');
export const fetchPlanResponse = createAction('payments/fetchPlanResponse');
// export const configResponse = createAction<PayloadAction<ConfigResponse>>("payments/configResponse");
export const setUserOnboarding = createAction<PayloadAction<Partial<MemberDetails>>>('payments/setOnboarding');
export const initiatePayment = (payload?: any, resolve?: any, reject?: any) => ({
  type: 'payments/initiatePayment',
  payload,
  meta: { resolve, reject },
});
export const loadUsersPlans = (payload?: any, resolve?: any, reject?: any) => ({
  type: 'payments/loadUsersPlans',
  payload,
  meta: { resolve, reject },
});

export default paymentsSlice.reducer;
